/*
 * A copy of the dispatch console's component of the same name.
 *
 * `17 WP16 Tenant Admin.dc.html` and `19 WP18 Dispatch Operations.dc.html` are the same file with
 * different content: their heading markup, their `hdrUI()`, their `gridUI()`, their `badge()` and
 * their palette are byte-identical, which was measured rather than assumed. So this component is
 * already built to this console's design file, and copying it changes nothing about what it draws.
 *
 * A copy rather than a shared package: see `docs/adr/0021-two-consoles-and-no-shared-shell.md`.
 */
import type { ReactNode } from "react";

/**
 * Where a console page says what it is: breadcrumbs, then the title.
 *
 * In the page body, above the heading — never in the top bar. The reference screenshots settle it:
 * `Console Shell/01 Shell light.jpg` puts `EAM › Operations › Active deliveries` immediately above
 * the `Active deliveries` heading, with the top bar carrying the tenant chip, search, the live pill,
 * help, the avatar and the bell and no crumbs at all. `04 Dispatch Exceptions Board` does the same.
 *
 * They were briefly in both places. The header kit was built first, from design rule 3's list —
 * "breadcrumbs, a help popover, a notifications bell, an account menu" — which names breadcrumbs as
 * part of the kit without saying where the kit puts them, and the shell design's own `hdrUI` does
 * define a `crumbBar` that neither console file renders. Reading the rule rather than the screen put
 * them in the top bar, and then the page rendered them too.
 *
 * One component so that cannot happen again: a page that wants a heading uses this, and nothing else
 * draws a crumb.
 */

export interface PageHeadingProps {
  /** Innermost last: tenant, section, page. */
  readonly crumbs: readonly string[];
  readonly title: string;
  /** The sentence under the title, where a page has one. */
  readonly description?: string;
  /** The right-hand side of the title row: actions, or a note about the operator's role. */
  readonly aside?: ReactNode;
  /**
   * The service codes behind the page, in the mono chip the design puts beside the title.
   *
   * `19 WP18 Dispatch Operations` draws one on every page — `DSP-01 · DSP-02` on the demand queue —
   * and it is the operator's way of naming what they are looking at when they report something.
   */
  readonly source?: string;
  /**
   * The page's primary command, at the far end of the title row.
   *
   * Inside the row rather than beside the whole heading, which is where the design puts it: level
   * with the title, not with the sentence below it.
   */
  readonly action?: ReactNode;
}

export function PageHeading({
  crumbs,
  title,
  description,
  aside,
  source,
  action,
}: PageHeadingProps): ReactNode {
  const heading = <h1 className="page-head__title">{title}</h1>;
  return (
    <div className="page-head">
      <div className="page-head__text">
        {crumbs.length === 0 ? null : (
          <nav className="page-head__crumbs" aria-label="Breadcrumb">
            {crumbs.map((crumb, index) => (
              <span key={crumb} className="page-head__crumb-part">
                <span
                  className={
                    index === crumbs.length - 1
                      ? "page-head__crumb page-head__crumb--here"
                      : "page-head__crumb"
                  }
                  {...(index === crumbs.length - 1 ? { "aria-current": "page" as const } : {})}
                >
                  {crumb}
                </span>
                {index === crumbs.length - 1 ? null : (
                  <span aria-hidden="true" className="page-head__crumb-sep">
                    chevron_right
                  </span>
                )}
              </span>
            ))}
          </nav>
        )}
        {/* The row exists only when something shares it, so a page with neither renders exactly the
            markup it rendered before this slot was added. */}
        {source === undefined && action === undefined ? (
          heading
        ) : (
          <div className="page-head__title-row">
            {heading}
            {source === undefined ? null : <span className="page-head__source">{source}</span>}
            {action === undefined ? null : (
              <>
                {/*
                  The design's own spacer element, not a `margin-left: auto`, and the difference is
                  visible at 390. A margin pins the command to the right edge of whichever line it
                  lands on; a growing spacer fills the first line instead, so when the command no
                  longer fits beside the title it wraps to the *start* of the next one, which is
                  where the design puts it.
                */}
                <span aria-hidden="true" className="page-head__spacer" />
                <span className="page-head__action">{action}</span>
              </>
            )}
          </div>
        )}
        {description === undefined ? null : <p className="page-head__description">{description}</p>}
      </div>
      {aside === undefined ? null : <div className="page-head__aside">{aside}</div>}
    </div>
  );
}
